import { Component } from '@angular/core';
import { Type } from 'class-transformer';
import { FormulaEditorDialog } from '../../../../components/common/formulaEditor/formulaEditor.control';
import { ClassTransformerHelper } from '../../../../helpers/class.transformer.helper';
import { VariablesNodeInformation } from '../../../../models/basic/formulaEditor.model';
import { FormulaInputMode } from '../../../../models/enums/formulainputmode.enum';
import { WorkflowType } from '../../../../models/enums/workflowtype.enum';
import { WorkflowExitInfo, WorkflowModuleSettingsHelper, WorkflowRegistry } from '../../../../services/workflow.service';
import { FormulaWorkflowDialogContent } from '../../../../workflow/workflow.dialog';

// Values over a group of objects, attached to every object of the group (groupValuesWFModule, 2026-10-22).
// Entry 0 collects inside a forEachWFModule loop over the object reader, entry 1 (from its done exit) emits the objects again.
@Component({
    selector: 'wf-groupvalues-settings',
    templateUrl: './groupvalues.settings.html',
})
export class GroupValuesSettings extends FormulaWorkflowDialogContent {
    Data = new GroupValuesSettingsData();
    Properties = [];
    Functions = [
        { Value: GroupValueFunction.Sum, Caption: '@@Summe' },
        { Value: GroupValueFunction.Count, Caption: '@@Anzahl' },
        { Value: GroupValueFunction.Min, Caption: '@@Minimum' },
        { Value: GroupValueFunction.Max, Caption: '@@Maximum' },
        { Value: GroupValueFunction.Average, Caption: '@@Durchschnitt' },
    ];

    public static GetRegistry(): WorkflowRegistry {
        const reg = new WorkflowRegistry();
        reg.ID = 'groupValuesWFModule';
        reg.Caption = '@@Gruppenwerte';
        reg.GroupID = 'objectoperations';
        reg.Index = 47;
        reg.SettingsControl = GroupValuesSettings;
        reg.SettingsTypeHelper = new GroupValuesSettingsDataHelper();
        reg.WorkflowType = WorkflowType.Service;
        return reg;
    }

    initialize(data: any) {
        super.initialize(data);
        const dsInfo = this.ActualState.get('DataSource');
        if (dsInfo) {
            this.Properties = JSON.parse(JSON.stringify(dsInfo.Properties));
        }
        if (data) {
            this.Data = ClassTransformerHelper.CopyObject(data, GroupValuesSettingsData);
        }
    }

    getResult(): any {
        this.Data.Values = this.Data.Values.filter(x => x.PropertyName);
        this.Data.Values.forEach(x => x.GroupBy = (x.GroupBy || []).filter(g => g));
        return this.Data;
    }

    addValue() {
        this.Data.Values.push(new GroupValueEntry());
    }

    removeValue(i: number) {
        this.Data.Values.splice(i, 1);
    }

    addGroup(entry: GroupValueEntry) {
        entry.GroupBy = [...(entry.GroupBy || []), ''];
    }

    removeGroup(entry: GroupValueEntry, i: number) {
        entry.GroupBy.splice(i, 1);
    }

    trackByIndex(i: number) {
        return i;
    }

    // field: 'Formula', 'Condition', 'TreeKey', 'TreeParent', 'TreeRoot' or a GroupBy index
    showFormulaDialog(entry: GroupValueEntry, index: number, field: string | number) {
        const variables = [...this.Variables];
        for (let i = 0; i < index; i++) {
            const previous = this.Data.Values[i];
            if (previous.PropertyName) {
                const vni = new VariablesNodeInformation();
                vni.Name = previous.PropertyName;
                variables.push(vni);
            }
        }
        this.Properties.forEach(stateProp => {
            const vni = new VariablesNodeInformation();
            vni.Name = stateProp.FullPath;
            variables.push(vni);
        });
        variables.sort((a, b) => a.Name < b.Name ? -1 : a.Name > b.Name ? 1 : 0);
        const formula = typeof field === 'number' ? entry.GroupBy[field] : entry[field];
        FormulaEditorDialog.ShowDialog({ Variables: variables, InputMode: FormulaInputMode.VariableName, Formula: formula }, result => {
            if (result && result.Formula) {
                if (typeof field === 'number') {
                    entry.GroupBy[field] = result.Formula;
                } else {
                    entry[field] = result.Formula;
                }
            }
            return true;
        });
    }
}

export enum GroupValueFunction {
    Sum = 0,
    Count = 1,
    Min = 2,
    Max = 3,
    Average = 4,
}

// @dynamic
export class GroupValuesSettingsData {
    @Type(() => GroupValueEntry)
    Values: GroupValueEntry[] = [];
}

export class GroupValueEntry {
    PropertyName: string;
    Function = GroupValueFunction.Sum;
    Formula: string;
    GroupBy: string[] = [];
    Condition: string;
    // Tree mode: aggregate over the subtree of TreeRoot (default: the object's own node).
    TreeKey: string;
    TreeParent: string;
    TreeRoot: string;
}

export class GroupValuesSettingsDataHelper extends WorkflowModuleSettingsHelper {
    getExitPoints(settings: any): WorkflowExitInfo[] {
        const info = new WorkflowExitInfo();
        info.Type = 'objData';
        return [info];
    }
    getEntryPoints(): WorkflowExitInfo[] {
        const def = new WorkflowExitInfo();
        def.Type = 'objData';
        const done = new WorkflowExitInfo();
        done.ID = 1;
        done.Label = '@@ForEach';
        done.Type = 'forEach';
        return [def, done];
    }
    getEmptySettingsInstance() {
        return new GroupValuesSettingsData();
    }
    async fillActualState(module, state, wfData) {
        if (state) {
            const settings = WorkflowModuleSettingsHelper.GetSettingsFromModule(module);
            const ds = state.get('DataSource');
            if (ds && ds.Properties && settings && settings.Values) {
                settings.Values.filter(x => x.PropertyName && !ds.Properties.some(p => p.FullPath === x.PropertyName)).forEach(x => {
                    ds.Properties.push({
                        Name: x.PropertyName,
                        Path: '',
                        FullPath: x.PropertyName,
                        ObjectProperties: [],
                        SimpleType: x.Function === GroupValueFunction.Count ? 'System.Int64' : 'System.Double'
                    });
                });
            }
        }
    }
}
